import axios from "axios";
import { apiUrl, logApiError, DEFAULT_SPECIALITY_CODE } from "@/shared/services/config.js";
import { SpecialityMasterResponse } from "@/shared/dtos/index.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";

// Speciality master. Rarely changes — we fetch each code at most once per tab and
// cache the request promise so concurrent callers share it. Successful results are
// mirrored into `specialityStore` for React subscribers; failed requests are
// evicted so a later call can retry.
const byCode = new Map();

/** @returns {Promise<SpecialityMasterResponse>} */
export const fetchSpecialityByCode = (code, opts = {}) => {
  if (byCode.has(code)) return byCode.get(code);
  const request = axios
    .get(apiUrl(`/auth/speciality/${encodeURIComponent(code)}`), { signal: opts.signal })
    .then((response) => {
      const dto = new SpecialityMasterResponse(response.data);
      specialityStore.setSpeciality(code, dto);
      return dto;
    })
    .catch((error) => {
      byCode.delete(code); // don't cache failures — allow a later retry
      logApiError(error);
      throw error;
    });
  byCode.set(code, request);
  return request;
};

/**
 * Return the speciality master id from the store, fetching the default SP code
 * (`/auth/speciality/SP_203`) first if it's not cached yet. Used by every
 * `/auth/quotedata` caller so the request always carries the authoritative
 * speciality id sourced from the speciality master.
 *
 * @param {string} [code] Defaults to `DEFAULT_SPECIALITY_CODE`.
 * @returns {Promise<number|null>}
 */
export const ensureSpecialityId = async (code = DEFAULT_SPECIALITY_CODE) => {
  const cached = specialityStore.currentSpeciality;
  if (cached?.id != null) return Number(cached.id);
  const dto = await fetchSpecialityByCode(code);
  return dto?.id != null ? Number(dto.id) : null;
};
